import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, MoreHorizontal, Pencil, RotateCcw, Trash2, CalendarDays, Wrench, Users, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { PRIORITY, dueLabel, completeTask, reopenTask, deleteTask, notifyTasksChanged } from "@/lib/tasks";
import { cn } from "@/lib/utils";

export const PriorityBadge = ({ priority, className }) => {
  const p = PRIORITY[priority] || PRIORITY.normal;
  return (
    <span className={cn("inline-flex h-5 items-center rounded-full border px-1.5 text-[10px] font-semibold uppercase tracking-wide", p.className, className)} data-testid={`task-priority-badge-${priority}`}>
      {p.short}
    </span>
  );
};

/**
 * TaskItem – eitt punkt i huskelista. Sjekk-sirkel (44px) til venstre, tekst i midten, meny til høgre.
 * compact: brukt på dashbord/maskinside (mindre meta).
 * onChange(updatedTask | null) – null = sletta.
 */
export const TaskItem = ({ task, onEdit, onChange, compact = false, showLinks = true }) => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = task.status === "done";
  const canDelete = isAdmin || task.created_by === user?.id;
  const due = dueLabel(task);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (busy) return;
    setBusy(true);
    try {
      const updated = done ? await reopenTask(task.id) : await completeTask(task.id);
      onChange?.(updated);
      notifyTasksChanged();
      if (!done) {
        toast.success("Fullført – bra jobba!", {
          action: { label: "Angre", onClick: async () => { try { const back = await reopenTask(task.id); onChange?.(back); notifyTasksChanged(); } catch (_) { /* noop */ } } },
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje oppdatere punktet.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await deleteTask(task.id);
      onChange?.(null);
      notifyTasksChanged();
      toast.success("Punktet er sletta.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikkje slette.");
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <div
        className={cn("group flex items-start gap-2 rounded-lg px-1 py-1.5 transition-colors hover:bg-accent/60 sm:gap-3", done && "opacity-70")}
        data-testid={`task-item-${task.id}`}
        data-status={task.status}
      >
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          aria-label={done ? "Opne punktet igjen" : "Marker som fullført"}
          aria-pressed={done}
          data-testid={`task-toggle-${task.id}`}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <span className={cn("grid h-6 w-6 place-items-center rounded-full border-2 transition-colors duration-150",
            done ? "border-transparent bg-[rgb(var(--status-ok))] text-white" : "border-muted-foreground/40 bg-card group-hover:border-primary")}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : done ? <Check className="h-4 w-4" strokeWidth={3} /> : null}
          </span>
        </button>

        <button type="button" onClick={() => onEdit?.(task)} className="min-w-0 flex-1 py-1.5 text-left" data-testid={`task-open-${task.id}`}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("text-[15px] font-semibold leading-tight", done && "line-through decoration-muted-foreground/60")} data-testid={`task-title-${task.id}`}>{task.title}</span>
            {task.priority !== "normal" && !done && <PriorityBadge priority={task.priority} />}
            {task.due_date && !done && (
              <span className={cn("inline-flex h-5 items-center gap-1 rounded-full border px-1.5 text-[11px] font-semibold tabular-nums",
                task.overdue ? "status-overdue" : task.days_remaining <= 1 ? "status-upcoming" : "border-border bg-card text-muted-foreground")} data-testid={`task-due-${task.id}`}>
                <CalendarDays className="h-3 w-3" /> {due || formatDate(task.due_date)}
              </span>
            )}
          </div>
          {task.description && !compact && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{task.description}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            {showLinks && task.machine_label && (
              <span role="link" tabIndex={0} onClick={(e) => { e.stopPropagation(); navigate(`/maskiner/${task.machine_id}`); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate(`/maskiner/${task.machine_id}`); } }} className="inline-flex cursor-pointer items-center gap-1 font-medium text-foreground hover:underline" data-testid={`task-machine-link-${task.id}`}>
                <Wrench className="h-3 w-3" /> {task.machine_label}
              </span>
            )}
            {showLinks && task.customer_name && (
              <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {task.customer_name}</span>
            )}
            {done ? (
              <span>Fullført {task.completed_at ? formatDate(task.completed_at) : ""}{task.completed_by_name ? ` av ${task.completed_by_name}` : ""}</span>
            ) : (
              !compact && <span>Lagt inn {formatDate(task.created_at)}{task.created_by_name ? ` av ${task.created_by_name}` : ""}</span>
            )}
          </div>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground hover:text-foreground" aria-label="Handlingar" data-testid={`task-menu-${task.id}`}>
              <MoreHorizontal className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem className="cursor-pointer" onClick={() => onEdit?.(task)} data-testid={`task-edit-${task.id}`}><Pencil className="mr-2 h-4 w-4" /> Rediger</DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" onClick={toggle} data-testid={`task-toggle-menu-${task.id}`}>
              {done ? <><RotateCcw className="mr-2 h-4 w-4" /> Opne igjen</> : <><Check className="mr-2 h-4 w-4" /> Marker fullført</>}
            </DropdownMenuItem>
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer text-destructive focus:text-destructive" onClick={() => setConfirmDelete(true)} data-testid={`task-delete-${task.id}`}><Trash2 className="mr-2 h-4 w-4" /> Slett</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette punktet?</AlertDialogTitle>
            <AlertDialogDescription>«{task.title}» blir fjerna frå huskelista for alle. Vurder å markere det som fullført i staden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="task-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
